import React from 'react';
import {
  Dropdown,
  Heading,
  HorizontalRule,
  Link,
  Sizes,
  TextBoldness,
  Typography,
  getClassNameFactory,
} from '@dcl/ui';
import { boldTypes, types } from '@dcl/ui/dist/elements/Heading';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';
import {
  IContactHubMultiSupportProps,
  ISelectedDropdownListItem,
} from './ContactHubMultiSupportTypes';

import './index.css';

import { formatDropdownValue, getPhoneSupportListForDropwdown } from './utils';
import { ContactHubPhoneSupport } from '../ContactHubPhoneSupport';
import { LinkTypes } from '@dcl/ui/dist/elements/Link';
import { NewTabIcon } from '../icons/NewTabIcon';
import {
  IMultiplePhoneSupportItem,
  IPhoneSupportCountryListItem,
  IPhoneSupportListItem,
} from './ContactHubTypes';
import { TECHNICAL_SUPPORT } from './constants';

export const ContactHubMultiSupport: React.FC<IContactHubMultiSupportProps> = (
  props
) => {
  const { dropdownLabel, dropdownItems, subHeaderLabel } = props;

  const typedDropdownItems = (dropdownItems ?? []) as (
    | IPhoneSupportListItem
    | IPhoneSupportCountryListItem
    | IMultiplePhoneSupportItem
  )[];
  const defaultSelectedItem =
    typedDropdownItems.find(
      (item) =>
        ('defaultSupportType' in item && item.defaultSupportType === true) ||
        ('defaultSupportTypeMultiplePhone' in item &&
          item.defaultSupportTypeMultiplePhone)
    ) ||
    typedDropdownItems.find((item) => {
      if ('supportType' in item) {
        return (
          item.supportType?.trim().toLocaleLowerCase() ===
          TECHNICAL_SUPPORT.trim().toLocaleLowerCase()
        );
      } else if ('countryName' in item) {
        return (
          item.countryName?.trim().toLocaleLowerCase() ===
          TECHNICAL_SUPPORT.trim().toLocaleLowerCase()
        );
      }
      return false;
    });
  const [
    selectedValue,
    setSelectedValue,
  ] = React.useState<ISelectedDropdownListItem>(
    formatDropdownValue(defaultSelectedItem) as ISelectedDropdownListItem
  );
  const [isSelected, setIsSelected] = React.useState(false);

  const handleValueChange = (value: any) => {
    setSelectedValue(formatDropdownValue(value));
    setIsSelected(true);
  };

  const getClassName = getClassNameFactory(ContactHubMultiSupport.displayName);

  return (
    <div>
      {isSelected && subHeaderLabel ? (
        <Typography
          type={Sizes.XXTRASMALL}
          boldness={TextBoldness.Medium}
          tag={TypographyTags.P}
          className={getClassName({ descendantName: 'subHeader' })}
        >
          {subHeaderLabel} : {selectedValue.selectedValue}
        </Typography>
      ) : null}
      <Typography
        type={Sizes.XXTRASMALL}
        boldness={TextBoldness.Bold}
        tag={TypographyTags.P}
        className={getClassName({
          descendantName:
            selectedValue.phoneSupportHeading && subHeaderLabel
              ? 'dropdownLabelWithMargin'
              : 'dropdownLabel',
        })}
      >
        {dropdownLabel}
      </Typography>
      {dropdownItems ? (
        <>
          <Dropdown
            block={true}
            items={getPhoneSupportListForDropwdown(dropdownItems)}
            placeholder={selectedValue.selectedValue as string}
            onValueChanged={(value) => handleValueChange(value)}
            withCheckmark
            className={getClassName({ descendantName: 'customDropdown' })}
            value={''}
          />
          <div
            className={getClassName({
              descendantName:
                dropdownItems.length > 3 ? 'dropdownExpand' : 'dropdownMargin',
              modifiers:
                dropdownItems.length <= 3 ? `${dropdownItems.length}` : '',
            })}
          />
        </>
      ) : null}

      {selectedValue ? (
        <>
          <HorizontalRule
            className={getClassName({ descendantName: 'divider' })}
          />
          <div
            className={getClassName({ descendantName: 'phoneSupportDetails' })}
          >
            <Heading
              className={getClassName({ descendantName: 'phoneTitle' })}
              type={types.Small}
              boldType={boldTypes.medium}
            >
              {selectedValue.phoneSupportHeading}
            </Heading>

            {selectedValue.phoneSupportDescription && (
              <div
                className={getClassName({
                  descendantName: 'phoneSupportDescription',
                })}
              >
                {selectedValue.phoneSupportDescription}
              </div>
            )}
          </div>

          {selectedValue.phoneSupportList?.map((phoneItem, index) => {
            const keyValue = `${phoneItem.phoneNumber}-${index + 1}`;
            return (
              <div
                key={keyValue}
                className={getClassName({ descendantName: 'phoneSupportList' })}
              >
                <ContactHubPhoneSupport
                  countryName={phoneItem.countryName}
                  phoneNumber={phoneItem.phoneNumber}
                  phoneText={phoneItem.phoneText}
                  timings={phoneItem.timings}
                  isFontLarge
                />
              </div>
            );
          })}

          <div className={getClassName({ descendantName: 'supportHoursLink' })}>
            {selectedValue.phoneSupportHoursList?.map((supportItem, index) => {
              const keyValue = `${supportItem.buttonLink}-${index + 1}`;

              return (
                <Link
                  key={keyValue}
                  href={supportItem.buttonLink}
                  data-testid={'supportHoursLink'}
                  type={LinkTypes.INLINE}
                  newWindow={supportItem.newTab}
                  iconRight={<NewTabIcon />}
                >
                  {supportItem.buttonLabel}
                </Link>
              );
            })}
          </div>
          {selectedValue.languageHeading && (
            <Heading
              className={getClassName({ descendantName: 'languageTitle' })}
              type={types.Medium}
              boldType={boldTypes.medium}
            >
              {selectedValue.languageHeading}
            </Heading>
          )}
          {selectedValue.languageList?.map((languageItem, index) => {
            const keyValue = `${languageItem.language}-${index + 1}`;
            return (
              <div
                key={keyValue}
                className={getClassName({ descendantName: 'languageList' })}
              >
                <Typography
                  type={Sizes.XXTRASMALL}
                  boldness={TextBoldness.Regular}
                  tag={TypographyTags.P}
                  className={getClassName({ descendantName: 'languageLine' })}
                >
                  <b>{`${languageItem.language}: `}</b>
                  {languageItem.languageOptions}
                </Typography>
              </div>
            );
          })}
        </>
      ) : null}
    </div>
  );
};

ContactHubMultiSupport.displayName = 'ContactHubMultiSupport';
